import { firstSentence, textExample } from './mcp-tool-help-text'
import {
  asNode,
  asText,
  enumValues,
  isContainer,
  isFile,
  isPlainObject,
  type Resolved,
  requiredList,
  resolve,
  type SchemaNode,
  titleOf,
  typesOf,
} from './mcp-tool-json-schema'
import { type LabelSource, labelLeaves } from './mcp-tool-labels'
import { type PickOneGroup, pickOneMessage, pickOnesOf, within } from './mcp-tool-pick-one'

export const REQUIRED_INPUT_MESSAGE = 'This field is required'

/** A tool's `input_schema` as saved on the node; it comes from the app, so read it defensively. */
export interface McpToolSchema {
  properties?: Record<string, unknown>
  required?: unknown
}

export type McpToolInputKind = 'text' | 'json' | 'select' | 'file' | 'files'

export interface McpToolInput {
  /** Property names from the root joined by `.` (`path.spreadsheetId`, `query.tweet.fields`). */
  key: string
  /** The property names themselves; one may contain a `.`, so `key` cannot be split back. */
  path: string[]
  label: string
  description?: string
  /** The description's first sentence, short enough to sit under the field. */
  summary?: string
  hints: string[]
  /** Needed on every call. */
  required: boolean
  /**
   * The optional group this input is required in: agent-runner sends the group once any input in
   * it is filled, and then this one must be filled too.
   */
  requiredWith?: string[]
  /** Where each pick-one rule above this input looks for filled inputs. */
  pickOneScopes: string[][]
  /** Part of a pick-one rule every call is checked against, so shown with the required inputs. */
  oneOfRequired: boolean
  kind: McpToolInputKind
  /** The values a `select` offers. */
  options: string[]
  placeholder: string
  defaultValue?: string
}

interface SchemaLeaf extends LabelSource {
  resolved: Resolved
  required: boolean
  requiredWith?: string[]
  /** Every pick-one group above the leaf, outermost first. */
  pickOnes: PickOneGroup[]
}

/** `group` is the nearest optional container above, or undefined when every one is required. */
const collectLeaves = (
  root: SchemaNode,
  container: Resolved,
  group: string[] | undefined,
  path: string[],
  containerTitles: (string | undefined)[],
  found: PickOneGroup[],
  outerPickOnes: PickOneGroup[] = [],
): SchemaLeaf[] => {
  const { schema } = container
  const listed = requiredList(schema)
  const visible = Array.isArray(schema.visible) ? schema.visible : undefined
  const own = pickOnesOf(root, container, path, group)
  found.push(...own)
  const pickOnes = [...outerPickOnes, ...own]
  return Object.entries(asNode(schema.properties)).flatMap(([key, raw]) => {
    const resolved = resolve(root, asNode(raw), container.refs)
    const requiredHere = listed.includes(key) || resolved.schema.required === true
    // agent-runner rejects a call that lacks a required input, offered or not.
    const offered =
      requiredHere || (resolved.schema.hidden !== true && (!visible || visible.includes(key)))
    if (!offered) return []
    const leafPath = [...path, key]
    const title = titleOf(resolved.schema)
    if (isContainer(resolved)) {
      const innerGroup = requiredHere ? group : leafPath
      const titles = [...containerTitles, title]
      return collectLeaves(root, resolved, innerGroup, leafPath, titles, found, pickOnes)
    }
    return [
      {
        path: leafPath,
        title,
        containerTitles,
        resolved,
        required: requiredHere && !group,
        requiredWith: requiredHere ? group : undefined,
        pickOnes,
      },
    ]
  })
}

const choicesOf = (schema: SchemaNode, types: string[]): string[] => {
  const values = enumValues(schema).map(String)
  if (values.length) return values
  const solid = new Set(types.filter((type) => type !== 'null'))
  return solid.size === 1 && solid.has('boolean') ? ['true', 'false'] : []
}

// A default outside the choices (GitHub's sort: "best match") is refused by the app on every run.
const defaultOf = (schema: SchemaNode, options: string[]) => {
  if (schema.default === undefined || schema.default === null) return undefined
  const text = asText(schema.default)
  return options.length && !options.includes(text) ? undefined : text
}

const MAX_EXAMPLE_DEPTH = 4

const exampleOf = (
  root: SchemaNode,
  resolved: Resolved,
  depth: number,
  inArray = false,
): unknown => {
  const { schema } = resolved
  const types = typesOf(root, resolved)
  const [choice] = enumValues(schema)
  if (choice !== undefined) return choice
  if (types.includes('array') && depth < MAX_EXAMPLE_DEPTH) {
    const item = exampleOf(
      root,
      resolve(root, asNode(schema.items), resolved.refs),
      depth + 1,
      true,
    )
    // A row of a table reads as a row only with more than one cell.
    return inArray ? [item, item] : [item]
  }
  if (types.includes('object')) {
    const properties = asNode(schema.properties)
    const keys = Object.keys(properties)
    if (resolved.cyclic || depth >= MAX_EXAMPLE_DEPTH || !keys.length) return { key: 'value' }
    const required = keys.filter((key) => requiredList(schema).includes(key))
    return Object.fromEntries(
      (required.length ? required : keys.slice(0, 1)).map((key) => [
        key,
        exampleOf(root, resolve(root, asNode(properties[key]), resolved.refs), depth + 1),
      ]),
    )
  }
  if (types.includes('string') || !types.length) return 'value'
  if (types.includes('number') || types.includes('integer')) return 1
  return types.includes('boolean') ? true : 'value'
}

const exampleText = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(exampleText).join(', ')}]`
  if (isPlainObject(value)) {
    const members = Object.entries(value).map(
      ([key, member]) => `${JSON.stringify(key)}: ${exampleText(member)}`,
    )
    return `{${members.join(', ')}}`
  }
  return JSON.stringify(value)
}

const kindOf = (
  root: SchemaNode,
  resolved: Resolved,
  types: string[],
  options: string[],
): McpToolInputKind => {
  if (options.length) return 'select'
  if (isFile(resolved)) return 'file'
  const items = resolve(root, asNode(resolved.schema.items), resolved.refs)
  // agent-runner downloads links only for a plain `type: "array"`, never a nullable one.
  if (resolved.schema.type === 'array' && isFile(items)) return 'files'
  return types.includes('array') || types.includes('object') ? 'json' : 'text'
}

const toInput = (root: SchemaNode, leaf: SchemaLeaf, label: string): McpToolInput => {
  const { schema } = leaf.resolved
  const types = typesOf(root, leaf.resolved)
  const options = choicesOf(schema, types)
  const kind = kindOf(root, leaf.resolved, types, options)
  const placeholders: Record<McpToolInputKind, () => string> = {
    select: () => 'Select a value',
    file: () => 'File URL or {{variable}}',
    files: () => 'One file URL or {{variable}} per line',
    json: () => exampleText(exampleOf(root, leaf.resolved, 0)),
    text: () => {
      const example = textExample(schema, types)
      if (example !== undefined) return `e.g. ${example} or {{variable}}`
      return types.includes('integer') || types.includes('number')
        ? 'A number or {{variable}}'
        : 'Text or {{variable}}'
    },
  }
  // A file's description is written for an AI agent sending base64; the builder gives a link.
  const isFileKind = kind === 'file' || kind === 'files'
  const description =
    !isFileKind && typeof schema.description === 'string' ? schema.description : undefined
  const defaultValue = defaultOf(schema, options)
  const { pickOnes } = leaf
  // Ancestors differ in depth, so a depth names the nearest container with a choice.
  const nearestDepth = pickOnes.filter((group) => group.hinted).at(-1)?.path.length
  const hints = pickOnes
    .filter((group) => group.hinted && group.path.length === nearestDepth)
    .map(pickOneMessage)
  const named = (group: PickOneGroup) =>
    [...group.shared, ...group.alternatives.flat()].some((key) => within(leaf.path, key.path))
  return {
    key: leaf.path.join('.'),
    path: leaf.path,
    label,
    description,
    summary: firstSentence(description),
    hints: [...new Set(hints)],
    required: leaf.required,
    requiredWith: leaf.requiredWith,
    pickOneScopes: pickOnes.map((group) => group.activatedBy ?? group.path),
    oneOfRequired: pickOnes.some((group) => !group.activatedBy && named(group)),
    kind,
    options,
    placeholder:
      !leaf.required && defaultValue !== undefined
        ? `Default: ${defaultValue}`
        : placeholders[kind](),
    defaultValue,
  }
}

export const inputsWithin = (inputs: McpToolInput[], path: string[]) =>
  inputs.filter((input) => within(input.path, path))

/** The other inputs in the optional group `input` is required in. */
export const groupMates = (input: McpToolInput, inputs: McpToolInput[]) =>
  input.requiredWith ? inputsWithin(inputs, input.requiredWith).filter((o) => o !== input) : []

/** The inputs whose value can change whether `input` is valid. */
export const linkedInputs = (input: McpToolInput, inputs: McpToolInput[]) => {
  const pickOneMates = input.pickOneScopes.flatMap((scope) => inputsWithin(inputs, scope))
  return [...new Set([...groupMates(input, inputs), ...pickOneMates])].filter((o) => o !== input)
}

const MAX_NAMED = 3

const listWithOr = (labels: string[]) => {
  // Naming a fourth input reads better than "or 1 more".
  const named =
    labels.length > MAX_NAMED + 1
      ? [...labels.slice(0, MAX_NAMED), `${labels.length - MAX_NAMED} more`]
      : labels
  return named.length > 1 ? `${named.slice(0, -1).join(', ')} or ${named.at(-1)}` : named[0]
}

const walkTool = (schema: McpToolSchema | null | undefined) => {
  const root = asNode(schema)
  const pickOnes: PickOneGroup[] = []
  const leaves = collectLeaves(root, resolve(root, root, []), undefined, [], [], pickOnes)
  const labels = labelLeaves(leaves)
  const drafts = leaves.map((leaf, index) => toInput(root, leaf, labels[index]))
  const inputs = drafts.map((input) => {
    const mates = groupMates(input, drafts)
    if (!mates.length) return input
    const when = `Required when ${listWithOr(mates.map((mate) => mate.label))} is filled`
    return { ...input, hints: [...input.hints, when] }
  })
  const required = inputs.filter((input) => input.required)
  return { inputs: [...required, ...inputs.filter((input) => !input.required)], pickOnes }
}

const described = new WeakMap<object, ReturnType<typeof walkTool>>()

// Each field's check asks for this on every keystroke. Safe to share per schema object: the walk
// reads only the schema, a schema is replaced rather than edited, and callers only read the result.
export const describeTool = (schema: McpToolSchema | null | undefined) => {
  if (!isPlainObject(schema)) return walkTool(schema)
  const description = described.get(schema) ?? walkTool(schema)
  described.set(schema, description)
  return description
}

export const getToolInputs = (schema: McpToolSchema | null | undefined): McpToolInput[] =>
  describeTool(schema).inputs

// An optional default would send its optional group, whose required inputs then turn mandatory
// unseen; left empty, the app applies the default itself.
export const initialInputsFor = (schema: McpToolSchema | null | undefined) =>
  Object.fromEntries(
    getToolInputs(schema).map((input) => [
      input.key,
      input.required ? (input.defaultValue ?? '') : '',
    ]),
  )
